import { create } from 'zustand';
import { PlatformApp } from '../types/index';
import { getApps } from '../api/apps';

// Feature #384 (Epic #381): which modules are switched on in this
// environment. Loaded once per session from GET /apps; `AppRoute` in
// App.tsx refuses to render a disabled module's pages and Settings › Apps
// (#385) writes back through `reload()` after a toggle.
//
// Failure is fail-open on purpose: if the registry cannot be read, every
// module is treated as enabled and the backend gate (#383) is the thing
// that actually says no. The SPA must never lock someone out of a working
// module because one boot-time request failed.
interface AppStoreState {
  apps: PlatformApp[];
  loaded: boolean;
  loading: boolean;
  // Bumped on every reload(). NavMenu refetches /navigation when it changes
  // so a toggle in Settings › Apps (#385) updates the sidebar without a
  // page reload — the backend drops a disabled module's rows (#383).
  navVersion: number;
  load: () => Promise<void>;
  reload: () => Promise<void>;
  isEnabled: (key: string) => boolean;
}

let inflight: Promise<void> | null = null;

export const useAppStore = create<AppStoreState>((set, get) => ({
  apps: [],
  loaded: false,
  loading: false,
  navVersion: 0,

  load: async () => {
    if (get().loaded) return;
    if (inflight) return inflight;
    inflight = get().reload();
    try {
      await inflight;
    } finally {
      inflight = null;
    }
  },

  reload: async () => {
    set({ loading: true });
    try {
      const apps = await getApps();
      set((s) => ({ apps, loaded: true, loading: false, navVersion: s.navVersion + 1 }));
    } catch {
      // Fail open — see module comment.
      set({ apps: [], loaded: true, loading: false });
    }
  },

  isEnabled: (key: string) => {
    const app = get().apps.find((a) => a.key === key);
    return app ? app.isEnabled : true;
  },
}));

// Convenience hook for components that only need one answer. Subscribes to
// the store so a toggle from Settings › Apps re-renders consumers.
export const useAppEnabled = (key: string): boolean =>
  useAppStore((s) => {
    const app = s.apps.find((a) => a.key === key);
    return app ? app.isEnabled : true;
  });
